import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import supabase from '../supabase/supabase';

const AnalyticsContext = createContext();

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) throw new Error('useAnalytics must be used within an AnalyticsProvider');
  return context;
};

export const AnalyticsProvider = ({ children }) => {
  const [rawAnalyticsData, setRawAnalyticsData] = useState({ pageViews: [], events: [], sessions: [], visitors: [], realTimeUsers: 0 });
  const [customUsers, setCustomUsers] = useState([]);
  const [excludedGroups, setExcludedGroups] = useState([]);
  const [trackingId] = useState(() => localStorage.getItem('intuitus_tid') || `IT-PRO-99`);

  const fetchCustomUsers = async () => {
    try {
      const { data } = await supabase
        .from('custom_users_1740000000000')
        .select('*')
        .order('created_at', { ascending: false });
      setCustomUsers(data || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  const fetchLiveTelemetry = async () => {
    try {
      // Fetch data from the new live ingestion tables
      const [pvRes, evRes] = await Promise.all([
        supabase.from('telemetry_page_views_1740000000012')
          .select('*')
          .order('timestamp', { ascending: false })
          .limit(2000),
        supabase.from('telemetry_events_1740000000012')
          .select('*')
          .order('timestamp', { ascending: false })
          .limit(2000)
      ]);

      if (pvRes.data || evRes.data) {
        // Map database snake_case back to camelCase for the frontend components
        const mappedViews = (pvRes.data || []).map(row => ({
          trackingId: row.tracking_id,
          visitorId: row.visitor_id,
          sessionId: row.session_id,
          userSerial: row.user_serial,
          url: row.url,
          page: row.page,
          referrer: row.referrer,
          device: row.device,
          viewport: { width: row.viewport_width, height: row.viewport_height },
          timestamp: new Date(row.timestamp).getTime(),
          eventType: 'pageview'
        }));

        const mappedEvents = (evRes.data || []).map(row => ({
          id: row.id,
          trackingId: row.tracking_id,
          visitorId: row.visitor_id,
          sessionId: row.session_id,
          userSerial: row.user_serial,
          eventName: row.event_name,
          page: row.page,
          properties: row.properties,
          timestamp: new Date(row.timestamp).getTime(),
          eventType: 'event'
        }));

        setRawAnalyticsData({
          pageViews: mappedViews,
          events: mappedEvents,
          sessions: [], 
          visitors: [], 
          realTimeUsers: 0
        });
      }
    } catch (err) {
      console.error("Telemetry fetch error:", err);
    }
  };

  useEffect(() => {
    fetchCustomUsers();
    fetchLiveTelemetry(); // Initial fetch
    
    // Poll database every 3 seconds for new live data
    const interval = setInterval(() => {
      fetchLiveTelemetry();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const analyticsData = useMemo(() => {
    if (excludedGroups.length === 0) return rawAnalyticsData;
    
    const excludedIdentities = new Set();
    customUsers.forEach(user => {
      if (excludedGroups.includes(user.user_group)) {
        excludedIdentities.add(user.user_serial);
        user.linked_visitor_ids?.forEach(vid => excludedIdentities.add(vid));
      }
    });

    const filterFn = (item) => !excludedIdentities.has(item.userSerial) && !excludedIdentities.has(item.visitorId);

    return {
      ...rawAnalyticsData,
      pageViews: rawAnalyticsData.pageViews.filter(filterFn),
      events: rawAnalyticsData.events.filter(filterFn)
    };
  }, [rawAnalyticsData, customUsers, excludedGroups]);

  const clearData = async () => {
    if(window.confirm("Are you sure you want to permanently delete all live telemetry data?")) {
      await supabase.from('telemetry_page_views_1740000000012').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('telemetry_events_1740000000012').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      fetchLiveTelemetry();
    }
  };

  const addCustomUser = async (user) => {
    const { error } = await supabase.from('custom_users_1740000000000').insert([
      {
        user_serial: user.userID,
        source_user_id: user.userID,
        first_name: user.firstName,
        last_name: user.lastName,
        postal_code: user.postalCode,
        state: user.state,
        membership_type: user.membershipType,
        membership_status: user.membershipStatus
      }
    ]);
    if (!error) fetchCustomUsers();
  };

  return (
    <AnalyticsContext.Provider value={{ 
      analyticsData, 
      rawAnalyticsData, 
      excludedGroups, 
      setExcludedGroups, 
      trackingId, 
      customUsers, 
      fetchCustomUsers, 
      addCustomUser,
      clearData
    }}>
      {children}
    </AnalyticsContext.Provider>
  );
};